import { z } from 'zod';

/**
 * Spreadsheet catalogue import.
 *
 * An accountant's workbook carries the shop's real prices, costs and on-hand
 * quantities. Importing it must UPDATE the matching product rather than add a
 * second one, and must show the owner exactly what will change first — so the
 * flow is upload → classify → preview → apply, with the classified rows living
 * in a staging batch between the preview and the apply.
 *
 * Nothing here converts money. Each price column carries its own currency and is
 * written into a price list denominated the same way.
 */

const MONEY = z.string().regex(/^-?\d+(\.\d{1,4})?$/);
const CURRENCY = z.string().length(3).regex(/^[A-Z]{3}$/);

/**
 * A boolean that survives a query string. `z.coerce.boolean()` is wrong here —
 * `Boolean('false')` is `true`, so a filter switched off would read as on.
 */
const BOOLISH = z.preprocess((v) => {
  if (typeof v === 'string') {
    if (['true', '1', 'yes'].includes(v.toLowerCase())) return true;
    if (['false', '0', 'no'].includes(v.toLowerCase())) return false;
  }
  return v;
}, z.boolean());

/** Which sheet column feeds which target. Fixed set — the sheet layout is known. */
export const IMPORT_COLUMNS = [
  'retailPrice',
  'marketPrice',
  'stationeryPrice',
  'unitCost',
  'quantity',
  'unit',
  'name',
  'barcode',
] as const;
export type ImportColumn = (typeof IMPORT_COLUMNS)[number];

/**
 * What the classifier decided a row is.
 * - `update` — its barcode is an existing product's own code or one of its aliases
 * - `create` — nothing answers to that barcode yet
 * - `skip_unit` — the code belongs to a PACKAGING unit (a carton barcode) of an
 *   existing product; creating a product for it would duplicate the catalogue
 * - `skip_duplicate` — the same barcode appeared earlier in the same sheet
 * - `error` — the row cannot be applied at all (see `message`)
 */
export const ImportRowActionSchema = z.enum([
  'create', 'update', 'skip_unit', 'skip_duplicate', 'error',
]);
export type ImportRowAction = z.infer<typeof ImportRowActionSchema>;

export const ImportRowStatusSchema = z.enum([
  'pending', 'processing', 'applied', 'skipped', 'failed', 'reverting', 'reverted',
]);
export type ImportRowStatus = z.infer<typeof ImportRowStatusSchema>;

export const ImportBatchStatusSchema = z.enum([
  'draft', 'applying', 'applied', 'cancelled', 'reverting', 'reverted',
]);
export type ImportBatchStatus = z.infer<typeof ImportBatchStatusSchema>;

/**
 * What one applied row displaced, recorded by the writer at the moment of
 * writing — the material a revert puts back.
 *
 * Why not reuse `before`: that snapshot is taken during CLASSIFICATION, before
 * the owner has even seen the preview. Between then and the apply the catalogue
 * can move (someone re-prices an item, a sale changes stock), and restoring from
 * it would write back a value that was never there. This one is captured inside
 * the apply transaction.
 *
 * Every restore is CONDITIONAL on the current stored value still being what the
 * import wrote — hence `wrote` beside each prior value. A price the owner
 * corrected by hand after the import is theirs, not the import's to take back.
 */
export interface ImportRowUndo {
  /** Set when the row CREATED this product — the revert retires it. */
  createdItemId?: string;
  /** Fields `items.update` actually patched, with what they held before. */
  item?: {
    name?: { prior: string | null; wrote: string };
    unitOfMeasure?: { prior: string | null; wrote: string };
    // The two money fields are nullable in `ItemUpdate` (an item can be sold with
    // no list price), so `wrote` has to be able to say "it was cleared".
    salePrice?: { prior: string | null; wrote: string | null };
    saleCurrencyCode?: { prior: string | null; wrote: string | null };
    purchasePrice?: { prior: string | null; wrote: string | null };
    purchaseCurrencyCode?: { prior: string | null; wrote: string | null };
  };
  /** One entry per price list written. `prior: null` = the row did not exist. */
  prices?: Array<{
    priceListId: string;
    prior: { unitPrice: string; currencyCode: string | null } | null;
    wrote: string;
  }>;
  /** The signed quantity this row posted, to be reversed movement-for-movement. */
  stockDelta?: string;
  /**
   * Did a stock level for (item, warehouse) exist BEFORE this row ran?
   *
   * Captured before the row writes anything, and that timing is the entire
   * point. Both the stock adjustment and the cost basis create a level when one
   * is missing, so by the time either of them looks, the answer is always "yes"
   * — and a revert reading it there leaves a zero-quantity row behind for every
   * product the import put on a shelf for the first time. Observed for real:
   * reverting Duhok's 4,881-row import put every quantity back correctly and
   * still left 3,782 empty rows on the stock page.
   */
  levelExisted?: boolean;
  /**
   * The cost basis it replaced. Whether the LEVEL existed is `levelExisted`
   * above, not anything here — by the time the cost is written the stock
   * adjustment has already created the row.
   */
  cost?: {
    priorCost: string | null;
    priorCurrency: string | null;
    wrote: string;
    wroteCurrency: string;
  };
}

/** Why a revert left something alone — shown per row, and counted per batch. */
export const ImportRevertOutcomeSchema = z.enum([
  /** Everything this row wrote was put back. */
  'reverted',
  /** The row wrote nothing, so there was nothing to undo. */
  'nothing_to_undo',
  /** Applied before undo tracking existed and not a `create` — cannot be undone. */
  'not_tracked',
  /** The product it created has since been sold or ordered; retiring it would
   *  orphan a real document, so it stays. */
  'item_in_use',
  /** Someone changed the value after the import; their edit wins. */
  'changed_since',
  /** Reversing the stock would take on-hand below zero — landed on zero instead. */
  'clamped_at_zero',
]);
export type ImportRevertOutcome = z.infer<typeof ImportRevertOutcomeSchema>;

/**
 * What the owner chose to apply.
 *
 * Every toggle defaults ON except nothing — they are all on — but each exists
 * because a column can be wrong or merely cosmetic in a given sheet (names in
 * particular differ on most rows for spacing alone), and declining one column
 * must not mean declining the import.
 */
export const ImportOptionsSchema = z.object({
  updatePrices: z.boolean().default(true),
  updateStock: z.boolean().default(true),
  updateCost: z.boolean().default(true),
  updateNames: z.boolean().default(true),
  updateUnits: z.boolean().default(true),
  /**
   * Add products the sheet has and the catalogue does not.
   *
   * Defaults OFF, and that default is the whole lesson of 2026-08-05: a
   * stocktake workbook whose barcodes came from somewhere other than this
   * catalogue matched half its rows and created the other half, doubling a
   * 2586-product shop to 5264 in two minutes. Matching is by barcode only, so
   * "not in the catalogue" really means "no product carries this barcode" —
   * which is equally true of a genuinely new product and of one the shop has
   * always stocked under a different code, and the importer cannot tell them
   * apart. Updating the wrong-but-existing product is recoverable; creating a
   * second copy of 2660 products is a week of someone's life.
   *
   * Turn it on deliberately, for a sheet you know is new stock.
   */
  createMissing: z.boolean().default(false),

  /** Currency of the retail column — written onto `items.sale_price`. */
  retailCurrencyCode: CURRENCY,
  /** Currency of the cost column — becomes the stock level's cost basis. */
  costCurrencyCode: CURRENCY,
  /** Price list that receives the market column; null = don't write it. */
  marketPriceListId: z.string().uuid().nullable().default(null),
  /** Price list that receives the stationery column; null = don't write it. */
  stationeryPriceListId: z.string().uuid().nullable().default(null),
});
export type ImportOptions = z.infer<typeof ImportOptionsSchema>;

/** Counts the preview and the progress bar are built from. */
export interface ImportSummary {
  total: number;
  create: number;
  update: number;
  skipUnit: number;
  skipDuplicate: number;
  error: number;
  pending: number;
  applied: number;
  skipped: number;
  failed: number;
  /** Rows whose catalogue writes have been put back. */
  reverted: number;
}

/** The product's values at classification time — the left half of the diff. */
export interface ImportRowBefore {
  name?: string | null;
  unit?: string | null;
  retailPrice?: string | null;
  retailCurrencyCode?: string | null;
  marketPrice?: string | null;
  stationeryPrice?: string | null;
  quantity?: string | null;
  unitCost?: string | null;
  costCurrencyCode?: string | null;
  /** Set on `skip_unit` — the product whose carton barcode this is. */
  ownerItemName?: string | null;
}

export interface ImportRow {
  id: string;
  rowNumber: number;
  barcode: string;
  name: string;
  unit: string | null;
  retailPrice: string | null;
  marketPrice: string | null;
  stationeryPrice: string | null;
  unitCost: string | null;
  quantity: string | null;
  action: ImportRowAction;
  matchedItemId: string | null;
  before: ImportRowBefore | null;
  /** False when every value in the row already matches what is stored. */
  hasChange: boolean;
  status: ImportRowStatus;
  message: string | null;
  /** Set once the row has been put back — see `ImportRevertOutcome`. */
  revertOutcome: ImportRevertOutcome | null;
}

export interface ImportBatch {
  id: string;
  filename: string;
  warehouseId: string;
  warehouseName: string | null;
  status: ImportBatchStatus;
  options: ImportOptions;
  rowCount: number;
  summary: ImportSummary;
  createdAt: string;
  appliedAt: string | null;
  revertedAt: string | null;
  /**
   * The stored workbook, downloadable at `/inventory-import/:id/file`. Null for
   * batches imported before the file was kept, or if storage refused it.
   */
  file: { attachmentId: string; sizeBytes: number } | null;
  /**
   * Whether a revert would do anything: the batch has applied rows that have not
   * already been put back. The UI asks this rather than reading `status`, so the
   * reason a Revert button is missing is one thing, not four.
   */
  canRevert: boolean;
  /**
   * Applied rows carrying no undo record — imported before this was tracked.
   * A revert still retires the products they CREATED (those are findable by
   * barcode) but cannot restore what they overwrote, and the page says so.
   */
  untrackedRows: number;
}

/** Query for the paginated preview table. */
export const ImportRowsQuerySchema = z.object({
  action: ImportRowActionSchema.optional(),
  status: ImportRowStatusSchema.optional(),
  /** Matches barcode or name. */
  q: z.string().max(200).optional(),
  /** Only rows whose values actually differ from the product's current ones. */
  changedOnly: BOOLISH.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ImportRowsQuery = z.infer<typeof ImportRowsQuerySchema>;

/** One `apply` call processes at most this many rows, then returns progress. */
export const ImportApplySchema = z.object({
  chunk: z.coerce.number().int().min(1).max(500).default(100),
});
export type ImportApplyInput = z.infer<typeof ImportApplySchema>;

export interface ImportApplyResult {
  /** Rows touched by THIS call. */
  processed: number;
  applied: number;
  skipped: number;
  failed: number;
  /** True when no pending rows remain. */
  done: boolean;
  summary: ImportSummary;
}

/**
 * Put a batch's catalogue writes back.
 *
 * Chunked for the same reason the apply is: a few thousand rows of stock
 * reversals and price restores cannot finish inside one request. Called
 * repeatedly until `done`.
 */
export const ImportRevertSchema = z.object({
  chunk: z.coerce.number().int().min(1).max(500).default(100),
});
export type ImportRevertInput = z.infer<typeof ImportRevertSchema>;

export interface ImportRevertResult {
  /** Rows touched by THIS call. */
  processed: number;
  /** Rows fully put back. */
  reverted: number;
  /** Rows deliberately left as they are — see `outcomes`. */
  kept: number;
  failed: number;
  done: boolean;
  /** Count per reason, accumulated across the whole revert. */
  outcomes: Record<ImportRevertOutcome, number>;
  /** Products retired so far, across the whole revert. */
  itemsRetired: number;
  summary: ImportSummary;
}

/** What deleting a batch should do with the changes it made. */
export const ImportDeleteQuerySchema = z.object({
  /**
   * Undo the catalogue writes before removing the record. Defaults ON: "remove
   * the import" means the shop expects the catalogue to go back with it, and a
   * deleted batch is the last chance to do it — once the rows are gone the undo
   * record is gone with them. Pass `false` to keep the changes and only forget
   * where they came from.
   */
  revert: BOOLISH.default(true),
});
export type ImportDeleteQuery = z.infer<typeof ImportDeleteQuerySchema>;

/** Everything the upload step needs, sent as query params alongside the file. */
export const ImportCreateSchema = z.object({
  warehouseId: z.string().uuid(),
  filename: z.string().min(1).max(255).default('import.xlsx'),
});
export type ImportCreateInput = z.infer<typeof ImportCreateSchema>;

// ---- Stock level list filters ----

/**
 * Stock-page filter for the quantity itself. `low` means "at or below the
 * threshold but still above zero" — the reorder shortlist, which is a different
 * question from "already out".
 */
/**
 * One column of the downloadable catalogue template.
 *
 * Served by the API rather than hardcoded in the web app: the headers a page
 * tells someone to type are only correct while the parser still accepts them,
 * and a stale guide fails silently — the column reads as absent, and a whole
 * price list is skipped without a word.
 */
export interface ImportTemplateColumn {
  /** Which field it feeds — `barcode`, `retailPrice`, … */
  key: string;
  /** The header as the template writes it, per script. */
  ar: string;
  en: string;
  sample: string;
}

export const StockStateSchema = z.enum(['all', 'inStock', 'outOfStock', 'negative', 'low']);
export type StockState = z.infer<typeof StockStateSchema>;

export const StockLevelsQuerySchema = z.object({
  itemId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  /** Item code, name, or barcode. */
  q: z.string().max(200).optional(),
  state: StockStateSchema.optional(),
  lowThreshold: MONEY.optional(),
  categoryId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  /** true = only levels carrying a cost basis, false = only those without. */
  hasCost: BOOLISH.optional(),
  sort: z.enum(['item', 'warehouse', 'quantity', 'value', 'updatedAt']).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type StockLevelsQuery = z.infer<typeof StockLevelsQuerySchema>;
